/**
 * Logger básico mejorado
 * 
 * Proporciona niveles de logging y facilita el cambio a un sistema
 * más avanzado en el futuro (Sentry, LogRocket, etc.)
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private enableVerbose =
    import.meta.env.DEV ||
    String((import.meta as any).env?.VITE_ENABLE_VERBOSE_LOGS ?? '').toLowerCase() === 'true';
  private logs: LogEntry[] = [];

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const prefix = this.getLevelPrefix(level);
    return `${prefix} [${timestamp}] ${message}`;
  }

  private getLevelPrefix(level: LogLevel): string {
    const prefixes = {
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      debug: '🔍',
    };
    return prefixes[level];
  }

  private log(level: LogLevel, message: string, data?: any) {
    const formattedMessage = this.formatMessage(level, message, data);
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };

    // Guardar en memoria (útil para debugging)
    if (this.isDevelopment) {
      this.logs.push(entry);
      // Limitar a últimas 100 entradas para evitar memoria excesiva
      if (this.logs.length > 100) {
        this.logs.shift();
      }
    }

    // Enviar a console
    switch (level) {
      case 'info':
        if (this.enableVerbose) console.warn(formattedMessage, data || '');
        break;
      case 'warn':
        console.warn(formattedMessage, data || '');
        break;
      case 'error':
        console.error(formattedMessage, data || '');
        break;
      case 'debug':
        if (this.enableVerbose) {
          console.warn(formattedMessage, data || '');
        }
        break;
    }

    // En producción, aquí podrías enviar a un servicio externo
    // if (!this.isDevelopment && level === 'error') {
    //   this.sendToErrorTracking(entry);
    // }
  }

  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  error(message: string, error?: any) {
    this.log('error', message, error);
  }

  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  // Método para obtener logs (útil en desarrollo)
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Método para limpiar logs
  clearLogs() {
    this.logs = [];
  }

  // Método para exportar logs (útil para debugging)
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

const logger = new Logger();

export const logInfo = (message: string, data?: any) => logger.info(message, data);
export const logWarn = (message: string, data?: any) => logger.warn(message, data);
export const logError = (message: string, error?: any) => logger.error(message, error);
export const logDebug = (message: string, data?: any) => logger.debug(message, data);
